/* GENERATED FILE — DO NOT EDIT.
 *
 * Emitted from `contracts/openapi/remediation.yaml` by `tools/openapi_ts/generate.py`.
 * Regenerate with `python -m tools.openapi_ts.generate`;
 * `test_generated_client_matches_the_contract` fails the build if this file drifts from the spec.
 *
 * Hard rule 10 makes the spec authoritative, so editing this file by hand would put the client and
 * the server behind two different sources of truth — which is the drift the rule exists to prevent.
 *
 * Each type has a matching `is<Name>` guard. The types are for `tsc`; the guards are because a
 * response is data from another process, and a compile-time type is only a claim about it.
 */


export type RemediationMode = 'advise' | 'emit' | 'execute';

export const REMEDIATION_MODES: readonly RemediationMode[] = ['advise', 'emit', 'execute'];

export function isRemediationMode(value: unknown): value is RemediationMode {
  return (['advise', 'emit', 'execute'] as readonly unknown[]).includes(value);
}

/**
 * `emitted` and `executed` are deliberately **not** terminal: an emitted script can still be
 * executed, and an executed plan is waiting to be verified or rolled back. Marking either terminal
 * would close the record at the moment the interesting half starts.
 */
export type PlanState = 'drafted' | 'simulated' | 'awaiting_approval' | 'approved' | 'rejected' | 'emitted' | 'executed' | 'verified' | 'rolled_back' | 'abandoned';

export const PLAN_STATES: readonly PlanState[] = ['drafted', 'simulated', 'awaiting_approval', 'approved', 'rejected', 'emitted', 'executed', 'verified', 'rolled_back', 'abandoned'];

export function isPlanState(value: unknown): value is PlanState {
  return (['drafted', 'simulated', 'awaiting_approval', 'approved', 'rejected', 'emitted', 'executed', 'verified', 'rolled_back', 'abandoned'] as readonly unknown[]).includes(value);
}

/**
 * `X` never appears in a response. A Tier X finding is recorded as Tier D with the reason, because
 * the domain refuses to construct a plan holding an X proposal and the database refuses the row.
 */
export type FixTier = 'A' | 'B' | 'C' | 'D';

export const FIX_TIERS: readonly FixTier[] = ['A', 'B', 'C', 'D'];

export function isFixTier(value: unknown): value is FixTier {
  return (['A', 'B', 'C', 'D'] as readonly unknown[]).includes(value);
}

/** The four legitimate sources. There is no fifth, and adding one is an ADR. */
export type ValueSource = 'mechanical_derivation' | 'reference_lookup' | 'cross_source_copy' | 'formula_recomputation';

export const VALUE_SOURCES: readonly ValueSource[] = ['mechanical_derivation', 'reference_lookup', 'cross_source_copy', 'formula_recomputation'];

export function isValueSource(value: unknown): value is ValueSource {
  return (['mechanical_derivation', 'reference_lookup', 'cross_source_copy', 'formula_recomputation'] as readonly unknown[]).includes(value);
}

/**
 * `set_null` and `delete_row` exist in the domain vocabulary and never appear here: both are Tier
 * X. They are in the vocabulary so that proposing one is refused loudly rather than being
 * expressible only as a recompute-to-nothing.
 */
export type Operation = 'trim' | 'uppercase' | 'lowercase' | 'normalise_whitespace' | 'reference_map' | 'copy_from_source' | 'recompute';

export const OPERATIONS: readonly Operation[] = ['trim', 'uppercase', 'lowercase', 'normalise_whitespace', 'reference_map', 'copy_from_source', 'recompute'];

export function isOperation(value: unknown): value is Operation {
  return (['trim', 'uppercase', 'lowercase', 'normalise_whitespace', 'reference_map', 'copy_from_source', 'recompute'] as readonly unknown[]).includes(value);
}

/**
 * Supplied from the catalog's classification, never inferred from a column name. Identity,
 * monetary and consent are Tier X regardless of what is being done to them.
 */
export type ColumnRole = 'ordinary' | 'identity' | 'monetary' | 'consent';

export const COLUMN_ROLES: readonly ColumnRole[] = ['ordinary', 'identity', 'monetary', 'consent'];

export function isColumnRole(value: unknown): value is ColumnRole {
  return (['ordinary', 'identity', 'monetary', 'consent'] as readonly unknown[]).includes(value);
}

export type ArtifactFormat = 'sql_script' | 'markdown_summary';

export const ARTIFACT_FORMATS: readonly ArtifactFormat[] = ['sql_script', 'markdown_summary'];

export function isArtifactFormat(value: unknown): value is ArtifactFormat {
  return (['sql_script', 'markdown_summary'] as readonly unknown[]).includes(value);
}

/**
 * What would be done and where the value comes from. Not constructible without a `value_source`,
 * which is how "Lumiq never invents a value" is enforced by the type rather than remembered by a
 * person.
 */
export interface FixAction {
  readonly operation: Operation;
  readonly value_source: ValueSource;
  /**
   * Whether the derivation is certain. `false` is fuzzy and forces Tier C no matter how legitimate
   * the source. Describes the *derivation*, not the completeness of the repair: trimming `" MH "`
   * certainly yields `"MH"`, and whether `"MH"` is accepted is a separate question the rule
   * re-answers on verification.
   */
  readonly exact: boolean;
  /**
   * The reference table, source system or formula. Required for every source except mechanical
   * derivation — a trim needs no citation, a copy does.
   */
  readonly reference?: string | null;
}

export function isFixAction(value: unknown): value is FixAction {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (['trim', 'uppercase', 'lowercase', 'normalise_whitespace', 'reference_map', 'copy_from_source', 'recompute'] as readonly unknown[]).includes(candidate['operation']) &&
    (['mechanical_derivation', 'reference_lookup', 'cross_source_copy', 'formula_recomputation'] as readonly unknown[]).includes(candidate['value_source']) &&
    typeof candidate['exact'] === 'boolean'
  );
}

export interface Proposal {
  readonly id: string;
  /**
   * Required. A remediation with no finding behind it is a change this platform decided to make on
   * its own, and it is also what makes verification meaningful — verification re-runs the rule
   * that produced this violation.
   */
  readonly violation_id: string;
  readonly rule_id: string;
  readonly qualified_name: string;
  readonly column: string;
  readonly tier: FixTier;
  readonly column_role: ColumnRole;
  /** The sentence an approver reads. A fix with no stated reason cannot be reviewed. */
  readonly rationale: string;
  /**
   * Always present, `null` when unsimulated — which is a different fact from `0`, meaning
   * simulated and found nothing. Collapsing the two would let an unmeasured plan show an approver
   * a confident zero.
   */
  readonly simulated_rows: number | null;
  /**
   * `null` at Tier D and only at Tier D: the correct value is not derivable, so there is no source
   * to name and no action to construct.
   */
  readonly action: FixAction | null;
}

export function isProposal(value: unknown): value is Proposal {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['id'] === 'string' &&
    typeof candidate['violation_id'] === 'string' &&
    typeof candidate['rule_id'] === 'string' &&
    typeof candidate['qualified_name'] === 'string' &&
    typeof candidate['column'] === 'string' &&
    (['A', 'B', 'C', 'D'] as readonly unknown[]).includes(candidate['tier']) &&
    (['ordinary', 'identity', 'monetary', 'consent'] as readonly unknown[]).includes(candidate['column_role']) &&
    typeof candidate['rationale'] === 'string' &&
    (typeof candidate['simulated_rows'] === 'number' || candidate['simulated_rows'] === null) &&
    (candidate['action'] === null || isFixAction(candidate['action']))
  );
}

export interface ProposalPreview {
  readonly proposal_id: string;
  readonly qualified_name: string;
  readonly column: string;
  /**
   * Tier X is absent because it is unrepresentable here: a plan carrying one is refused with
   * `unsafe_tier` before any preview is built.
   */
  readonly tier: 'A' | 'B' | 'C' | 'D';
  /**
   * Rows this fix would actually change — `col IS DISTINCT FROM <derivation>`, counted through the
   * read-only pool. Carried from the approved simulation on a refusal, so a reader sees what was
   * intended without this platform having touched the source to produce it.
   */
  readonly rows_to_change: number;
  /**
   * Rows the rule flagged that this fix would not touch. The number that says a proposal is aimed
   * wrong, and the one a single total hides: 400,000 flagged with 12 changed is not a fix.
   */
  readonly rows_unresolved: number;
  readonly operation: string;
  readonly value_source: string;
  /**
   * Where the value is derived from. All three of these are **mechanism rather than datum** —
   * which transformation, which source, which citation — so a reader learns what would happen
   * without learning what is in the row. There is no field here a customer value fits in.
   */
  readonly reference: string | null;
}

export function isProposalPreview(value: unknown): value is ProposalPreview {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['proposal_id'] === 'string' &&
    typeof candidate['qualified_name'] === 'string' &&
    typeof candidate['column'] === 'string' &&
    (['A', 'B', 'C', 'D'] as readonly unknown[]).includes(candidate['tier']) &&
    typeof candidate['rows_to_change'] === 'number' &&
    typeof candidate['rows_unresolved'] === 'number' &&
    typeof candidate['operation'] === 'string' &&
    typeof candidate['value_source'] === 'string' &&
    (typeof candidate['reference'] === 'string' || candidate['reference'] === null)
  );
}

export interface ExecutionAssessment {
  readonly plan_id: string;
  readonly assessed_at: string;
  /**
   * Whether a write could proceed **if** a write adapter existed. False on every deployment today,
   * because `no_write_adapter` is itself a refusal. Not a permission to act.
   */
  readonly executable: boolean;
  /**
   * The **first** blocking reason, or null. First rather than all of them: a list of seven
   * problems reads as "this is hopeless" where the first one is the thing to do next. Each member
   * is a different next step. `not_approved` needs a second signature; `wrong_state` needs a
   * different plan; `not_simulated` means an approver would be signing over unknown counts;
   * `unsafe_tier` is Tier X, refused at five layers; `not_reversible` means the source offers no
   * undo, so emit the script instead; `out_of_region` is a residency decision; `no_write_adapter`
   * is a deployment.
   */
  readonly refusal: string | null;
  /**
   * Whether the refusal is about the **deployment** rather than this plan. True only for
   * `no_write_adapter`. Separates "fix your plan" from "this platform cannot execute anything"
   * without a client matching on the reason's text.
   */
  readonly terminal: boolean;
  /**
   * Host-written, one line, naming the next step. Never a driver message, never a value, and never
   * composed by a caller — the same bound `IntegrityFinding.detail` carries, because this field is
   * persisted in the audit snapshot and served over HTTP.
   */
  readonly detail: string;
  /**
   * The **worst** undo across every connection this plan touches, not the best. A plan spanning a
   * source with time travel and one with none cannot be wholly undone, and reporting the better
   * half would be a promise about the wrong table.
   */
  readonly rollback_strategy: 'source_native_time_travel' | 'customer_staging_table' | 'customer_object_store' | 'lumiq_object_store' | 'none_available';
  /** The total across every preview. The number an approver signed for. */
  readonly rows_to_change: number;
  /**
   * Proposals execution would **not** touch — Tier D findings with no safe action. Reported rather
   * than omitted, because a partial fix must not read as a complete one. The emitted script names
   * them as comments for the same reason.
   */
  readonly flagged_proposals: readonly string[];
  readonly previews: readonly ProposalPreview[];
}

export function isExecutionAssessment(value: unknown): value is ExecutionAssessment {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['plan_id'] === 'string' &&
    typeof candidate['assessed_at'] === 'string' &&
    typeof candidate['executable'] === 'boolean' &&
    (['not_approved', 'wrong_state', 'not_simulated', 'unsafe_tier', 'not_reversible', 'out_of_region', 'no_write_adapter', null] as readonly unknown[]).includes(candidate['refusal']) &&
    typeof candidate['terminal'] === 'boolean' &&
    typeof candidate['detail'] === 'string' &&
    (['source_native_time_travel', 'customer_staging_table', 'customer_object_store', 'lumiq_object_store', 'none_available'] as readonly unknown[]).includes(candidate['rollback_strategy']) &&
    typeof candidate['rows_to_change'] === 'number' &&
    Array.isArray(candidate['flagged_proposals']) &&
    (candidate['flagged_proposals'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    Array.isArray(candidate['previews']) &&
    (candidate['previews'] as readonly unknown[]).every((item) => isProposalPreview(item))
  );
}

export interface PlanSignature {
  readonly approver: string;
  readonly decided_at: string;
  /**
   * False for a rejection. One rejection closes the plan, so a list holding one is a list holding
   * the whole story.
   */
  readonly approved: boolean;
  /**
   * What the approver typed. Carried because it is what an incident review a year later reads, and
   * reducing it here would decide for every client that they may not show it.
   */
  readonly reason: string;
}

export function isPlanSignature(value: unknown): value is PlanSignature {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['approver'] === 'string' &&
    typeof candidate['decided_at'] === 'string' &&
    typeof candidate['approved'] === 'boolean' &&
    typeof candidate['reason'] === 'string'
  );
}

export interface Plan {
  readonly id: string;
  readonly execution_id: string;
  readonly dataset_id: string;
  /**
   * A plan is a statement about a specific schema state. Keyed on the version so a plan built
   * against columns that have since been dropped does not read as current.
   */
  readonly dataset_version_id: string;
  readonly mode: RemediationMode;
  readonly state: PlanState;
  /**
   * The token subject. A string, not a UUID — OIDC defines `sub` as a StringOrURI, and Keycloak,
   * Entra and Okta each emit a different shape.
   */
  readonly created_by: string;
  readonly created_at: string;
  readonly simulated_at: string | null;
  readonly decided_by: string | null;
  readonly decided_at: string | null;
  readonly approval_request_id: string | null;
  /**
   * Where the caller stands on signing this plan. `may_sign` is the only value on which a client
   * should offer Approve or Reject. `already_signed` is a receipt rather than a refusal — the
   * caller has signed and the remaining signatures must come from other people, which is the whole
   * of an N-eyes policy; presenting it as a refusal tells somebody their own successful approval
   * was turned away. `refused` is segregation of duties: the requester, or a delegation chain that
   * reaches them, and it is permanent for that person on this request. `not_open` means there is
   * no decision to take, which is not a statement about the caller. None of these is derivable
   * from this payload. The requester is recorded on the approval request rather than on the plan,
   * `created_by` is the planner on anything drafted automatically, and the delegation chain is
   * published nowhere.
   */
  readonly decision_standing: 'may_sign' | 'already_signed' | 'refused' | 'not_open';
  /**
   * The sentence explaining `decision_standing`, or null for `may_sign` and `not_open`, where
   * nothing is being withheld from anybody in particular. Render this rather than composing your
   * own: the two refusals send a reader to different places, and a greyed-out control with no
   * sentence reads as a broken screen.
   */
  readonly decision_detail: string | null;
  /**
   * Every decision recorded on this plan's approval request, oldest first — not only the one that
   * satisfied it. The plan's own `decided_by` is written by the tipping signature, so a client
   * deriving history from that field alone names the second approver of a two-eyes plan and loses
   * the first. Empty for a plan whose state cannot carry a decision; read it with
   * `approval_request_id` to tell "no request was opened" from "nobody has signed yet".
   */
  readonly signatures: readonly PlanSignature[];
  /** Rows the applicable proposals would touch, from the simulation. Zero before it runs. */
  readonly estimated_rows: number;
  readonly applicable_count: number;
  /**
   * Tier D findings — reported and never applied. Surfaced as its own count because "we found 40
   * bad rows and can fix none of them" is the answer a steward most needs, and the one a
   * fix-shaped API hides.
   */
  readonly flagged_count: number;
  readonly proposals: readonly Proposal[];
}

export function isPlan(value: unknown): value is Plan {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['id'] === 'string' &&
    typeof candidate['execution_id'] === 'string' &&
    typeof candidate['dataset_id'] === 'string' &&
    typeof candidate['dataset_version_id'] === 'string' &&
    (['advise', 'emit', 'execute'] as readonly unknown[]).includes(candidate['mode']) &&
    (['drafted', 'simulated', 'awaiting_approval', 'approved', 'rejected', 'emitted', 'executed', 'verified', 'rolled_back', 'abandoned'] as readonly unknown[]).includes(candidate['state']) &&
    typeof candidate['created_by'] === 'string' &&
    typeof candidate['created_at'] === 'string' &&
    (typeof candidate['simulated_at'] === 'string' || candidate['simulated_at'] === null) &&
    (typeof candidate['decided_by'] === 'string' || candidate['decided_by'] === null) &&
    (typeof candidate['decided_at'] === 'string' || candidate['decided_at'] === null) &&
    (typeof candidate['approval_request_id'] === 'string' || candidate['approval_request_id'] === null) &&
    (['may_sign', 'already_signed', 'refused', 'not_open'] as readonly unknown[]).includes(candidate['decision_standing']) &&
    (typeof candidate['decision_detail'] === 'string' || candidate['decision_detail'] === null) &&
    Array.isArray(candidate['signatures']) &&
    (candidate['signatures'] as readonly unknown[]).every((item) => isPlanSignature(item)) &&
    typeof candidate['estimated_rows'] === 'number' &&
    typeof candidate['applicable_count'] === 'number' &&
    typeof candidate['flagged_count'] === 'number' &&
    Array.isArray(candidate['proposals']) &&
    (candidate['proposals'] as readonly unknown[]).every((item) => isProposal(item))
  );
}

export interface Artifact {
  readonly format: ArtifactFormat;
  readonly filename: string;
  /** SHA-256 of `content`, recorded at emission and served with the download. */
  readonly checksum: string;
  readonly content: string;
}

export function isArtifact(value: unknown): value is Artifact {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (['sql_script', 'markdown_summary'] as readonly unknown[]).includes(candidate['format']) &&
    typeof candidate['filename'] === 'string' &&
    typeof candidate['checksum'] === 'string' &&
    typeof candidate['content'] === 'string'
  );
}

/**
 * One emitted artifact, named and digested. **Deliberately without `content`** -- see the listing
 * route. `Artifact` is the shape the emit response returns and does carry the script, because the
 * caller who emitted it is the caller who asked for it.
 */
export interface ArtifactSummary {
  readonly format: ArtifactFormat;
  readonly filename: string;
  /**
   * SHA-256 recorded at emission and served with the download, so "the script we approved" and
   * "the script in the record" are comparable.
   */
  readonly checksum: string;
  readonly emitted_at: string;
}

export function isArtifactSummary(value: unknown): value is ArtifactSummary {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (['sql_script', 'markdown_summary'] as readonly unknown[]).includes(candidate['format']) &&
    typeof candidate['filename'] === 'string' &&
    typeof candidate['checksum'] === 'string' &&
    typeof candidate['emitted_at'] === 'string'
  );
}

/**
 * A rendering of the plan, for reading. **Not an artifact**: nothing is stored, nothing is
 * digested, and `Artifact` is the shape that carries a checksum because it is the shape whose
 * bytes somebody signed for.
 */
export interface ScriptPreview {
  readonly format: ArtifactFormat;
  /**
   * The name the emitted file would carry. Offered so a reader knows what to look for afterwards,
   * and deliberately not as a download -- this response is text on a screen.
   */
  readonly filename: string;
  /** The rendered script or summary, byte for byte as the emitter produced it. */
  readonly content: string;
  readonly plan_state: PlanState;
  /**
   * Whether the plan already carries its two signatures, and therefore whether the header's
   * approval line names real approvers. **False is the ordinary case for this route** -- it exists
   * to be read before approval -- and a client must say so rather than presenting an unapproved
   * rendering as the approved script.
   */
  readonly approved: boolean;
}

export function isScriptPreview(value: unknown): value is ScriptPreview {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (['sql_script', 'markdown_summary'] as readonly unknown[]).includes(candidate['format']) &&
    typeof candidate['filename'] === 'string' &&
    typeof candidate['content'] === 'string' &&
    (['drafted', 'simulated', 'awaiting_approval', 'approved', 'rejected', 'emitted', 'executed', 'verified', 'rolled_back', 'abandoned'] as readonly unknown[]).includes(candidate['plan_state']) &&
    typeof candidate['approved'] === 'boolean'
  );
}
